import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Container, Box, Typography, Card, CardContent, Button, Alert, CircularProgress } from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import { apiService, API_EXECUTION_USAGE_EVENT } from '../services/apiService';

const ApiExecutionAddonPaymentSuccess: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);
  const [packCount, setPackCount] = useState<number | null>(null);

  const sessionId = searchParams.get('session_id');

  useEffect(() => {
    if (sessionId) {
      verifyPayment(sessionId);
    } else {
      setError('Missing session ID. Please check your payment confirmation email or contact support.');
      setLoading(false);
    }
  }, [sessionId]);

  const verifyPayment = async (id: string) => {
    try {
      setLoading(true);
      setError(null);
      const data = await apiService.verifyApiExecutionAddonPayment(id);

      if (data.error) {
        setError(data.error);
        setLoading(false);
        return;
      }

      if (data.message) {
        setSuccess(true);
        if (data.packCount) {
          setPackCount(parseInt(data.packCount, 10));
        }
        window.dispatchEvent(new Event(API_EXECUTION_USAGE_EVENT));
        setLoading(false);

        setTimeout(() => {
          const token = localStorage.getItem('token');
          const user = localStorage.getItem('user');
          if (token && user) {
            navigate('/manage-account?tab=subscription&refresh=true', { replace: true });
          } else {
            const returnUrl = encodeURIComponent('/manage-account?tab=subscription&refresh=true');
            navigate(`/login?returnUrl=${returnUrl}`, { replace: true });
          }
        }, 3000);
      } else {
        setError('Invalid response from server. Please contact support.');
        setLoading(false);
      }
    } catch (err: any) {
      const errorMsg =
        err.response?.data?.error ||
        err.response?.data?.message ||
        err.message ||
        'An error occurred while verifying payment.';
      setError(errorMsg);
      setLoading(false);
    }
  };

  return (
    <Container maxWidth="sm">
      <Box sx={{ py: 8, textAlign: 'center' }}>
        {loading && !error && !success && (
          <Card>
            <CardContent>
              <CircularProgress sx={{ mb: 2 }} />
              <Typography variant="h6" gutterBottom>
                Verifying payment...
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Please wait while we activate your API execution add-on
              </Typography>
            </CardContent>
          </Card>
        )}

        {success && (
          <Card>
            <CardContent>
              <CheckCircleIcon sx={{ fontSize: 80, color: 'success.main', mb: 2 }} />
              <Typography variant="h4" gutterBottom>
                Add-on Activated!
              </Typography>
              <Typography variant="body1" color="text.secondary" paragraph>
                {packCount
                  ? `You subscribed to ${packCount} pack${packCount > 1 ? 's' : ''} (+${(packCount * 25000).toLocaleString()} API executions/month).`
                  : 'Your API execution add-on subscription is active.'}
              </Typography>
              <Alert severity="info" sx={{ mt: 2, mb: 2, textAlign: 'left' }}>
                <Typography variant="body2">
                  This is a monthly subscription ($29/pack). It renews automatically until cancelled in Stripe or via support.
                </Typography>
              </Alert>
              <Button
                variant="contained"
                onClick={() => navigate('/manage-account?tab=subscription&refresh=true', { replace: true })}
                sx={{ mt: 2 }}
              >
                Go to Subscription
              </Button>
            </CardContent>
          </Card>
        )}

        {error && (
          <Card>
            <CardContent>
              <Alert severity="error" sx={{ mb: 2 }}>
                {error}
              </Alert>
              <Button
                variant="contained"
                onClick={() => navigate('/manage-account?tab=subscription', { replace: true })}
              >
                Back to Subscription
              </Button>
            </CardContent>
          </Card>
        )}
      </Box>
    </Container>
  );
};

export default ApiExecutionAddonPaymentSuccess;
